//@ts-ignore
//@ts-nocheck
import { AxiosResponse } from 'axios';
import { FormikHelpers } from 'formik';
import invariant from 'invariant';
import {
  all,
  call,
  delay,
  put,
  select,
  take,
  takeLeading,
} from 'redux-saga/effects';
import {
  apiRequestFailed,
  apiRequestSuccess,
  invalidateLoadedResourceIfPresent,
  requestDataIfNecessary,
} from 'services/api/api.actions';
import apiRequest from 'services/api/api.config';
import { unprocessableEntity_422 } from 'services/api/api.statusCodes';
import { formErrorKey, genericErrorId } from 'services/forms/forms.config';
import { getUserProfileEndpoint } from 'services/user/user.config';
import {
  makeSelectModifyProfileContactsApiConfig,
  makeSelectModifyProfileEmailApiConfig,
  makeSelectModifyProfilePhoneNumberApiConfig,
  makeSelectModifyProfileUsernameApiConfig,
  makeSelectModifyUserDomicileApiConfig,
  makeSelectModifyUserPasswordApiConfig,
  selectGetUserProfileApiConfig,
} from 'services/user/user.selectors';
import mapValidationErrors from 'utils/functions/mapValidationErrors';
import {
  setUsernameSuggestions,
  updateLatestSubmissionOutcome,
  updateSubmittingForm,
  userRequestedProfileUpdate,
} from './profile.actions';
import {
  addressCodeFieldName,
  emailFieldName,
  feedbackPersistanceDurationMs,
  idDocumentTypeFieldName,
  mapPayloadKeyToFieldName,
  municipalityFieldName,
  nationFieldName,
  passwordFieldName,
  phoneFieldName,
  phonePrefixName,
  postalCodeFieldName,
  ProfileForms,
  usernameFieldName,
} from './profile.config';
import {
  UserContactsFormValues,
  UserDomicileFormValues,
  UserEmailFormValues,
  UserPhoneNumberFormValues,
} from './profile.types';

import { createIsApiActionOf } from 'services/api/api.helpers';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';

function* handleProfileModifiedSuccessfully() {
  const userProfileApiConfig: RequestApiDataIfNecessaryPayload = yield select(
    selectGetUserProfileApiConfig,
  );

  yield put(
    invalidateLoadedResourceIfPresent({ resourceId: getUserProfileEndpoint }),
  );
  yield put(requestDataIfNecessary(userProfileApiConfig));

  // Await the resolution of all the requests made before moving on.
  // We want to resolve this saga only when all the requests are settled.
  yield all([
    take([
      createIsApiActionOf(apiRequestSuccess, getUserProfileEndpoint),
      createIsApiActionOf(apiRequestFailed, getUserProfileEndpoint),
    ]),
  ]);
}

function* handlePostSubmit(
  fetchError: any,
  validationErrors: Record<string, string> | undefined,
  action: ReturnType<typeof userRequestedProfileUpdate>,
) {
  const {
    payload: { formikHelpers, profileFormType },
  } = action;

  formikHelpers.setSubmitting(false);

  if (!fetchError) {
    formikHelpers.resetForm();
  }

  // We add a bit of  delay to ensure a smoother UI behaviour.
  yield delay(50);

  yield put(updateSubmittingForm(null));

  let errors: Record<string, string> | null = null;

  if (validationErrors) {
    errors = validationErrors;
  } else if (fetchError) {
    errors = { [formErrorKey]: genericErrorId };
  }

  yield put(
    updateLatestSubmissionOutcome({
      profileForm: profileFormType,
      errors,
    }),
  );

  yield delay(feedbackPersistanceDurationMs);
  yield put(updateLatestSubmissionOutcome({ profileForm: null, errors: null }));
}

function* handleProfileModifiedFailed(
  fetchError: any,
  formikHelpers: FormikHelpers<any>,
  documentType?: number,
) {
  let apiErrors;

  if (fetchError.response?.data) {
    const errorResponse = fetchError.response as AxiosResponse<any>;

    if (errorResponse.status === unprocessableEntity_422) {
      apiErrors = mapValidationErrors(errorResponse, mapPayloadKeyToFieldName);

      if (apiErrors) {
        formikHelpers.setErrors(apiErrors);

        const suggestedUsernames = errorResponse?.data?.proposalUsernameList;

        if (
          Array.isArray(suggestedUsernames) &&
          suggestedUsernames.length > 0
        ) {
          yield put(setUsernameSuggestions(suggestedUsernames));
        }
      }
    }
  }

  return apiErrors;
}

function* handleUserAccessDataFormSubmit({
  payload,
}: ReturnType<typeof userRequestedProfileUpdate>) {
  const username = payload.formValues[usernameFieldName];

  invariant(
    typeof username === 'string' && username,
    `handleUserAccessDataFormSubmit: invalid username "${username}"`,
  );

  const { requestConfig } = yield select(
    makeSelectModifyProfileUsernameApiConfig(username),
  );

  const response = yield call(apiRequest, requestConfig);

  return response;
}

function* handleUserPasswordFormSubmit({
  payload,
}: ReturnType<typeof userRequestedProfileUpdate>) {
  const newPassword = payload.formValues[passwordFieldName];

  invariant(
    typeof newPassword === 'string' && newPassword,
    `handleUserAccessDataFormSubmit: invalid password provided`,
  );

  const { requestConfig } = yield select(
    makeSelectModifyUserPasswordApiConfig(newPassword),
  );

  const response = yield call(apiRequest, requestConfig);

  return response;
}

function* handleUserDomicileSubmit({
  payload,
}: ReturnType<typeof userRequestedProfileUpdate>) {
  const formValues = payload.formValues as UserDomicileFormValues;

  const { requestConfig } = yield select(
    makeSelectModifyUserDomicileApiConfig({
      country: formValues[nationFieldName],
      municipality: formValues[municipalityFieldName],
      address: formValues[addressCodeFieldName],
      postalCode: formValues[postalCodeFieldName],
    }),
  );

  const response = yield call(apiRequest, requestConfig);

  return response;
}

function* handleUserContactsSubmit({
  payload,
}: ReturnType<typeof userRequestedProfileUpdate>) {
  const {
    [phoneFieldName]: phoneNumber,
    [phonePrefixName]: phonePrefix,
    [emailFieldName]: email,
  } = payload.formValues as UserContactsFormValues;

  const { requestConfig } = yield select(
    makeSelectModifyProfileContactsApiConfig({
      phoneNumber,
      phonePrefix,
      email,
    }),
  );

  const response = yield call(apiRequest, requestConfig);

  return response;
}

function* handleUserPhoneNumberSubmit({
  payload,
}: ReturnType<typeof userRequestedProfileUpdate>) {
  const { [phoneFieldName]: phoneNumber, [phonePrefixName]: phonePrefix } =
    payload.formValues as UserPhoneNumberFormValues;

  const { requestConfig } = yield select(
    makeSelectModifyProfilePhoneNumberApiConfig({
      phoneNumber,
      phonePrefix,
    }),
  );

  const response = yield call(apiRequest, requestConfig);

  return response;
}

function* handleUserEmailSubmit({
  payload,
}: ReturnType<typeof userRequestedProfileUpdate>) {
  const { [emailFieldName]: newEmail } =
    payload.formValues as UserEmailFormValues;

  const { requestConfig } = yield select(
    makeSelectModifyProfileEmailApiConfig({
      newEmail,
    }),
  );

  const response = yield call(apiRequest, requestConfig);

  return response;
}

export function* handleUserProfileSubmit(
  action: ReturnType<typeof userRequestedProfileUpdate>,
) {
  const { profileFormType, formikHelpers } = action.payload;
  let profileUpdateError;
  let validationErrors;

  try {
    try {
      switch (profileFormType) {
        case ProfileForms.accessDataForm:
          yield call(handleUserAccessDataFormSubmit, action);
          break;
        case ProfileForms.passwordForm:
          yield call(handleUserPasswordFormSubmit, action);
          break;
        case ProfileForms.domicileForm:
          yield call(handleUserDomicileSubmit, action);
          break;
        case ProfileForms.sensitiveDataForm:
          yield call(handleUserSecurityQuestionSubmit, action);
          break;
        case ProfileForms.contactsForm:
          yield call(handleUserContactsSubmit, action);
          break;
        case ProfileForms.phoneForm:
          yield call(handleUserPhoneNumberSubmit, action);
          break;
        case ProfileForms.mailForm:
          yield call(handleUserEmailSubmit, action);
          break;
      }
    } catch (fetchError) {
      profileUpdateError = fetchError;
    }
    if (profileUpdateError) {
      if (
        action.payload.profileFormType ===
        ProfileForms.identificationDocumentForm
      ) {
        validationErrors = yield call(
          handleProfileModifiedFailed,
          profileUpdateError,
          formikHelpers,
          action.payload.formValues[idDocumentTypeFieldName],
        );
      } else {
        validationErrors = yield call(
          handleProfileModifiedFailed,
          profileUpdateError,
          formikHelpers,
        );
      }
    } else {
      yield call(handleProfileModifiedSuccessfully);
    }
  } finally {
    yield call(handlePostSubmit, profileUpdateError, validationErrors, action);
  }
}

export default function* rootProfileSaga() {
  yield all([
    takeLeading(userRequestedProfileUpdate.type, handleUserProfileSubmit),
  ]);
}
